import { colors } from '@galapagos/design-system/colors';
import { radius } from '@galapagos/design-system/radius';
import { sizing } from '@galapagos/design-system/sizing';
import { spacing } from '@galapagos/design-system/spacing';
import { typography } from '@galapagos/design-system/typography';

type TypographyToken = keyof typeof typography;

export const WRAP_UP_STYLE_ID = 'signup-wrap-up-style';

function font(token: TypographyToken): string {
  const t = typography[token];
  return `font-family: ${t.fontFamily}; font-weight: ${t.fontWeight}; font-size: ${t.fontSize}px; line-height: ${t.lineHeight}px; letter-spacing: ${t.letterSpacing}px;`;
}

/** The picture panels' fills, by the v9 frame each one comes from. */
export const WRAP_UP_PANEL_TONES = {
  // 1.13-A.1, the walking illustration's cream.
  cream: colors.coral['100'],
  // 1.13-B.1, the rewards phone's pink.
  pink: colors.punchPink['200'],
  // 1.13-B.2, the welcome-call photo's grey.
  grey: colors.background.primary,
  // 1.14-A.2, the registered page's pale green.
  green: colors.mossGreen['300'],
} as const;

/**
 * The registered page's check circle. v9 draws it 56px, which is not a library
 * size, so it is composed from the touch-target and small spacing tokens.
 */
export const REGISTERED_CHECK_SIZE = sizing.touchTarget.minimum + spacing.s;

export type WrapUpPanelTone = keyof typeof WRAP_UP_PANEL_TONES;

/**
 * The drawn height of each panel's picture. v9 draws the walking illustration
 * 169px tall and the two phone pictures 230px and 262px; the nearest library
 * sizes are the illustration panel height and the large illustration size.
 */
export const WRAP_UP_PANEL_IMAGE_HEIGHTS = {
  illustration: sizing.illustration.panelHeight,
  phone: sizing.illustration.lg,
} as const;

/**
 * Layout for the wrap-up steps: one column of content, then the actions. The
 * same structure serves phone and desktop, since the page column already
 * centres and pads it. A picture sits centred on a rounded panel of its v9
 * fill. The router's loading state stacks a spinner over its status line in
 * the middle of the page; once it fails, that wrapper drops out of the layout.
 * Every value is a design-system token.
 */
export function wrapUpCss(): string {
  return `
.signup-step { display: flex; flex-direction: column; gap: ${spacing.m}px; }
.signup-step__text { margin: 0; color: ${colors.text.primary}; ${font('b3Regular')} }
.signup-step__body { margin: 0; color: ${colors.text.primary}; ${font('b2Regular')} }
.signup-step__note { margin: 0; color: ${colors.text.secondary}; ${font('c2Regular')} }
.signup-step__status { margin: 0; color: ${colors.text.secondary}; ${font('b3Regular')} }
.signup-step__status[data-tone='error'] { color: ${colors.text.alert}; }
.signup-wrap-up__loading[data-state='loading'] { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: ${spacing.m}px; text-align: center; }
.signup-wrap-up__loading[data-state='failed'] { display: contents; }
.signup-wrap-up__panel { display: flex; justify-content: center; align-items: center; padding: ${spacing.lg}px; border-radius: ${radius.xl}px; }
${Object.entries(WRAP_UP_PANEL_TONES)
  .map(([tone, fill]) => `.signup-wrap-up__panel[data-tone='${tone}'] { background: ${fill}; }`)
  .join('\n')}
.signup-wrap-up__panel[data-tone='green'] { padding: ${spacing.xxl}px ${spacing.lg}px; }
.signup-wrap-up__panel img { display: block; width: auto; max-width: 100%; }
.signup-wrap-up__panel img[data-size='illustration'] { height: ${WRAP_UP_PANEL_IMAGE_HEIGHTS.illustration}px; }
.signup-wrap-up__panel img[data-size='phone'] { height: ${WRAP_UP_PANEL_IMAGE_HEIGHTS.phone}px; }
`;
}

/** Injects the wrap-up stylesheet once per document. No-op during prerender. */
export function ensureWrapUpStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(WRAP_UP_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = WRAP_UP_STYLE_ID;
  style.textContent = wrapUpCss();
  document.head.appendChild(style);
}
